/**
 * ProbePanel (P282.3) -- implementation probes for a control + Test of
 * Implementation derivation. A probe is a categorical live-data signal
 * (present / partial / absent / unknown) computed from the twin
 * (connector findings / evidence freshness / CMDB / drills / attestations);
 * ToI derives deterministically from the probes' statuses -- implementation
 * observed, not asserted. The status VALUES refresh from the twin probe
 * sampler; this panel defines the probes + triggers the derivation.
 */
import { InfoTip } from "shared/components/Explainer";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useControlProbes,
  useCreateProbe,
  useDeleteProbe,
  useDeriveToI,
} from "framework/api/probe_hooks";
import { StatusPill, type PillTone } from "shared/components/StatusPill";
import { useToast } from "shared/toast/ToastProvider";
import type { ControlProbe, ProbeStatus } from "shared/api/types";

const STATUS_TONE: Record<ProbeStatus, PillTone> = {
  present: "green",
  partial: "amber",
  absent: "red",
  unknown: "neutral",
};

interface Props {
  cycleId: string;
  controlId: string;
}

export function ProbePanel({ cycleId, controlId }: Props): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const probes = useControlProbes(controlId);
  const deriveToI = useDeriveToI(cycleId, controlId);

  const runDerive = async () => {
    try {
      const res = await deriveToI.mutateAsync();
      toast.push({
        tone: "success",
        message: t("modules.framework.probe.toiDerived", {
          result: t(`modules.framework.controlTesting.result.${res.test.result}`),
        }),
      });
    } catch (e) {
      const err = e as { status?: number };
      toast.push({
        tone: "warning",
        message:
          err.status === 422
            ? t("modules.framework.probe.noProbes")
            : t("modules.framework.probe.deriveError"),
      });
    }
  };

  const list = probes.data?.probes ?? [];

  return (
    <section data-testid="probe-panel" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-muted">
          {t("modules.framework.probe.heading")}
          <InfoTip termKey="probe" />
        </h4>
        <button
          type="button"
          onClick={runDerive}
          disabled={deriveToI.isPending || list.length === 0}
          data-testid="probe-derive-toi"
          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark disabled:opacity-50"
        >
          {t("modules.framework.probe.deriveToI")}
        </button>
      </div>

      {list.length === 0 && (
        <p data-testid="probe-empty" className="text-xs text-ink-2">
          {t("modules.framework.probe.empty")}
        </p>
      )}

      {list.map((probe) => (
        <ProbeRow key={probe.id} probe={probe} controlId={controlId} />
      ))}

      <AddProbeForm controlId={controlId} />
    </section>
  );
}

function ProbeRow({
  probe,
  controlId,
}: {
  probe: ControlProbe;
  controlId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const deleteProbe = useDeleteProbe(controlId);

  return (
    <div
      data-testid={`probe-row-${probe.id}`}
      className="flex items-center justify-between gap-2 rounded border border-paper-2 p-2 text-xs"
    >
      <span className="font-medium text-ink">{probe.name}</span>
      <div className="flex items-center gap-2">
        <StatusPill tone={STATUS_TONE[probe.last_status]}>
          {t(`modules.framework.probe.status.${probe.last_status}`)}
        </StatusPill>
        <button
          type="button"
          onClick={() => deleteProbe.mutate(probe.id)}
          data-testid={`probe-delete-${probe.id}`}
          className="text-rag-red hover:underline"
        >
          {t("modules.framework.probe.remove")}
        </button>
      </div>
    </div>
  );
}

function AddProbeForm({ controlId }: { controlId: string }): JSX.Element {
  const { t } = useTranslation();
  const probes = useControlProbes(controlId);
  const createProbe = useCreateProbe(controlId);
  const [open, setOpen] = useState(false);
  const [probeKey, setProbeKey] = useState("");

  const catalogue = probes.data?.catalogue ?? [];

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="probe-add-toggle"
        className="text-xs text-brand-brassDark hover:underline"
      >
        {t("modules.framework.probe.addProbe")}
      </button>
    );
  }

  const submit = () => {
    const key = probeKey || catalogue[0]?.key;
    if (!key) return;
    createProbe.mutate({ probe_key: key }, { onSuccess: () => setOpen(false) });
  };

  return (
    <div
      data-testid="probe-add-form"
      className="flex flex-wrap items-center gap-2 rounded border border-paper-2 p-2 text-xs"
    >
      <select
        value={probeKey}
        onChange={(e) => setProbeKey(e.target.value)}
        data-testid="probe-add-key"
        className="rounded border border-paper-2 bg-paper px-1 py-0.5"
      >
        <option value="">{t("modules.framework.probe.pickProbe")}</option>
        {catalogue.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={submit}
        data-testid="probe-add-submit"
        className="rounded bg-brand-brass px-2 py-0.5 text-brand-navy"
      >
        {t("modules.framework.probe.add")}
      </button>
    </div>
  );
}
